import api from "@/lib/api";
import {
  listOutbox,
  dequeue,
  markAttempt,
  pendingCount,
  pruneStaleOutbox,
  clearOptimistic,
  saveSnapshot,
} from "@/lib/outbox";

const SYNC_TAG = "haccp-outbox-sync";

/**
 * Ask the service worker to wake us up the moment connectivity returns
 * via the Background Sync API. Safe to call from anywhere — silently
 * no-ops on browsers that lack support (Safari < 18) where the existing
 * `online`/`focus` listeners in startSyncEngine still cover the case.
 */
export async function requestBackgroundSync() {
  if (typeof navigator === "undefined") return false;
  if (!("serviceWorker" in navigator)) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    if (!reg || !("sync" in reg)) return false;
    await reg.sync.register(SYNC_TAG);
    return true;
  } catch {
    return false; // permission denied, quota exceeded, or unsupported
  }
}

let listeners = new Set();
let syncing = false;

export function onSyncChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
async function notify() {
  const count = await pendingCount();
  for (const fn of listeners) fn({ online: navigator.onLine, pending: count, syncing });
}

export async function flushOutbox() {
  if (syncing || !navigator.onLine) return;
  syncing = true;
  notify();
  try {
    await pruneStaleOutbox();
    const items = await listOutbox();
    for (const it of items) {
      try {
        const config = {
          method: it.method,
          url: it.url,
          params: it.params,
          data: it.body,
        };
        const res = await api.request(config);
        // Server-wins: replace optimistic row with canonical server response.
        if (it.optimistic?.id) {
          await clearOptimistic(it.optimistic.id);
        }
        await dequeue(it.key);
        // optionally inform listeners that one synced
        notify();
        // server response can be used by callers if needed
        void res;
      } catch (err) {
        // 4xx auth/validation = drop (silent server-wins). 5xx/network = retry next cycle.
        const status = err?.response?.status;
        if (status && status >= 400 && status < 500) {
          if (it.optimistic?.id) await clearOptimistic(it.optimistic.id);
          await dequeue(it.key);
        } else {
          await markAttempt(it.key, err?.message || "network");
          break; // stop on first transient failure to preserve order
        }
      }
    }
  } finally {
    syncing = false;
    notify();
  }
}

export async function pullSnapshot(site_id) {
  if (!navigator.onLine || !site_id) return null;
  try {
    const { data } = await api.get(`/sites/${site_id}/snapshot`);
    await saveSnapshot(site_id, data);
    return data;
  } catch {
    return null;
  }
}

export function startSyncEngine(getActiveSiteId) {
  // Initial state announce
  notify();

  const tick = async () => {
    await flushOutbox();
    const sid = getActiveSiteId();
    if (sid) await pullSnapshot(sid);
  };

  // SW posts {type:'haccp-outbox-sync'} when the Background Sync API
  // fires on reconnect — even if the tab was backgrounded or the
  // `online` event was throttled (mobile Safari quirk).
  const onSwMessage = (event) => {
    if (event?.data?.type === "haccp-outbox-sync") {
      tick();
    }
  };
  if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", onSwMessage);
  }

  window.addEventListener("online", tick);
  window.addEventListener("offline", () => notify());
  window.addEventListener("focus", tick);

  // Periodic sync every 30s while online
  const id = setInterval(() => {
    if (navigator.onLine) tick();
    else notify();
  }, 30_000);

  return () => {
    clearInterval(id);
    window.removeEventListener("online", tick);
    window.removeEventListener("offline", notify);
    window.removeEventListener("focus", tick);
    if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
      navigator.serviceWorker.removeEventListener("message", onSwMessage);
    }
  };
}
